// frontend/src/contexts/AuthContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode
} from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import {
  supabase,
  SUPABASE_AUTH_TOKEN_KEY,
  AUTH_STORAGE_KEY,
  PKCE_CODE_VERIFIER_KEY,
  clearAuthParamsFromUrl,
} from '../lib/supabase';
import {
  initializeTokenManager,
  handleAuthStateChange as tokenManagerAuthStateChange,
  clearTokenCache,
  isPermanentRefreshError
} from '../lib/tokenManager';
import { clearAuthStorage, setLoggingOut } from '../utils/authErrorHandler';
import { subscribeToSignoutRequired } from '../utils/appEvents';
import { setSentryUser } from '../lib/sentry';
import { getErrorMessage } from '../utils/errorUtils';

// Initialize TokenManager with Supabase client on module load
initializeTokenManager(supabase);

// =============================================================================
// CONSTANTS
// =============================================================================

/** localStorage keys for user-specific data cleared on sign out */
const USER_STORAGE_KEYS = [
  'academic_planner_db_user',
  'academic_planner_setup_dismissed',
  'chatbot-enabled',
  'collapsedSemesters',
  'bookmarkedCollapsed',
  'commandHistory',
  // NOT clearing password_set - it's user-specific and should persist
];

/** Additional auth token keys cleared during error recovery */
const AUTH_TOKEN_KEYS = [
  SUPABASE_AUTH_TOKEN_KEY,
  AUTH_STORAGE_KEY,
  PKCE_CODE_VERIFIER_KEY,
  'academic_planner_beta_auth',
];

/** Clear all user/session storage related to the current session */
function clearUserStorage(includeAuthTokens = false): void {
  const keys = includeAuthTokens ? [...AUTH_TOKEN_KEYS, ...USER_STORAGE_KEYS] : USER_STORAGE_KEYS;
  keys.forEach(key => localStorage.removeItem(key));

  // Clear all sessionStorage (onboarding dismissal, pending password setup, etc.)
  sessionStorage.clear();
}

// =============================================================================
// TYPES
// =============================================================================

export interface AuthResult<T = unknown> {
  data: T | null;
  error: AuthError | Error | null;
}

export interface AuthContextValue {
  // Auth state
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;

  // Passwordless auth (auth redesign — docs/plans/auth-google-sso/). Identity
  // only — access is gated server-side by the provisioning gate.
  signInWithGoogle: () => Promise<AuthResult>;
  sendEmailOtp: (email: string, captchaToken?: string) => Promise<AuthResult>;
  verifyEmailOtp: (email: string, token: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;

  // Utility methods
  clearError: () => void;

  // Helper flags
  isAuthenticated: boolean;
  isLoading: boolean;
  hasError: boolean;
}

// =============================================================================
// CONTEXT
// =============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    let mounted = true;

    // Helper to handle invalid refresh token errors gracefully
    const handleInvalidRefreshToken = () => {
      console.warn('Invalid refresh token detected - signing out user');
      clearAuthStorage();
      clearTokenCache();

      if (mounted) {
        setUser(null);
        setSession(null);
        setLoading(false);
        // Don't set error - just redirect to login
      }
    };

    // Initialize auth by calling getSession once to trigger Supabase's
    // internal session restoration. The onAuthStateChange listener below
    // will receive the session and update our state.
    //
    // IMPORTANT: We don't use the result directly - we let onAuthStateChange
    // be the single source of truth to avoid race conditions.
    const initializeAuth = async () => {
      try {
        // Observe the auth-redirect outcome. `getSession()` awaits the SDK's
        // initialize promise but DISCARDS its error, so a failed callback was
        // previously invisible: no session, no error, no message, and the URL left
        // as-is. `initialize()` is the SDK's documented way to read that error
        // ("should also be called manually when checking for an error from an auth
        // redirect") and is memoized — it returns the very promise getSession()
        // awaits, so this adds no work and cannot race.
        //
        // Isolated in its own try/catch on purpose: this is a diagnostic, and it
        // must never be able to break sign-in. Without the guard an SDK that lacks
        // `initialize` (a downgrade, a partial test double) threw into the outer
        // catch and left the user unable to authenticate at all — which the test
        // suite caught.
        let redirectError: { message: string } | null = null;
        try {
          const initResult = await supabase.auth.initialize();
          redirectError = initResult?.error ?? null;
        } catch (initErr) {
          console.warn('Could not read auth-redirect outcome:', getErrorMessage(initErr));
        }

        const { data: { session }, error } = await supabase.auth.getSession();

        // A redirect that failed leaves auth material in the address bar, because
        // the SDK only cleans the URL on the success path.
        //
        // Deliberately NOT gated on `!session`. auth-js keeps an existing session
        // when a URL login fails ("Don't remove existing session on URL login
        // failure" — GoTrueClient), so an already-signed-in user who lands on an
        // implicit-shaped URL during the deploy transition has BOTH an error and a
        // session — and that is precisely the case where a live
        // `#access_token=…&refresh_token=…` is sitting in the address bar. Gating on
        // `!session` left the one sub-case this exists to fix. URL hygiene is
        // unconditional; only the user-facing messaging needs the no-session
        // distinction, and that lands with #377.
        if (redirectError) {
          console.warn('Auth redirect did not complete:', redirectError.message);
          clearAuthParamsFromUrl();
        }

        // Handle permanent refresh token errors during session retrieval
        if (error && isPermanentRefreshError(error)) {
          handleInvalidRefreshToken();
          return;
        }

        // Feed the session to TokenManager for caching
        // onAuthStateChange will also fire, but this ensures immediate availability
        if (session) {
          tokenManagerAuthStateChange('INITIAL_SESSION', session);
        }

        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error initializing auth:', err);

        // Handle permanent refresh token errors
        if (isPermanentRefreshError(err as Error)) {
          handleInvalidRefreshToken();
          return;
        }

        if (mounted) {
          setError(getErrorMessage(err));
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Set up global error handler for auth errors that occur during token refresh
    // This catches errors that happen in the background when Supabase tries to refresh tokens
    const handleAuthError = (event: Event | CustomEvent) => {
      const customEvent = event as CustomEvent;
      const err = customEvent?.detail || event;
      // Only handle permanent refresh errors - temporary ones are handled by TokenManager
      if (isPermanentRefreshError(err)) {
        handleInvalidRefreshToken();
      }
    };

    // Listen for Supabase auth errors on window (some versions emit these)
    window.addEventListener('supabase.auth.error', handleAuthError);

    // Listen for signout requests from other parts of the app (e.g., apiClient on 401)
    const unsubscribeSignoutRequired = subscribeToSignoutRequired(() => handleInvalidRefreshToken());

    // Listen for changes on auth state (sign in, sign out, etc.)
    // This is the PRIMARY source of truth for auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Feed every auth state change to TokenManager
      // This keeps the cached token in sync with Supabase's internal state
      tokenManagerAuthStateChange(event, session);

      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Update Sentry user context for error tracking
        setSentryUser(session?.user ?? null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      window.removeEventListener('supabase.auth.error', handleAuthError);
      unsubscribeSignoutRequired();
    };
  }, []);

  // --- Passwordless (auth redesign) ---------------------------------------
  // These establish IDENTITY only. Whether the identity gets an account is
  // decided server-side by the provisioning gate at /api/users/sync — an
  // un-granted identity lands on the NeedsProvisioning interstitial.

  const signInWithGoogle = useCallback(async (): Promise<AuthResult> => {
    try {
      setError(null);
      // Redirects to Google; on return, detectSessionInUrl (supabase.ts)
      // restores the session and onAuthStateChange drives the sync.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          // Always show Google's account picker — otherwise an existing Google
          // session silently signs the user into whatever account is active
          // (a problem for anyone with a personal + school Google account).
          queryParams: { prompt: 'select_account' },
        },
      });
      if (error) {
        setError(error.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      return { data: null, error: err as AuthError };
    }
  }, []);

  const sendEmailOtp = useCallback(async (email: string, captchaToken?: string): Promise<AuthResult> => {
    try {
      setError(null);
      // shouldCreateUser: true — OTP mints a Supabase identity for a new email
      // (identity != access; the gate still decides provisioning).
      // captchaToken: the Turnstile token, required once Supabase's CAPTCHA
      // protection is on (open-access plan §3.7). Only this request needs it.
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: captchaToken
          ? { shouldCreateUser: true, captchaToken }
          : { shouldCreateUser: true },
      });
      if (error) {
        setError(error.message);
        return { data: null, error };
      }
      return { data, error: null };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      return { data: null, error: err as AuthError };
    }
  }, []);

  const verifyEmailOtp = useCallback(async (email: string, token: string): Promise<AuthResult> => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
      if (error) {
        setError(error.message);
        return { data: null, error };
      }
      // Success establishes a session → onAuthStateChange → sync.
      return { data, error: null };
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      return { data: null, error: err as AuthError };
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      setLoading(true);

      // Mark that logout is in progress - prevents 401 errors from triggering error cascade
      setLoggingOut(true);

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear TokenManager cache
      clearTokenCache();

      // Clear any local state
      setUser(null);
      setSession(null);

      // Clear user-specific storage to prevent data leakage between users
      clearUserStorage();

    } catch (err) {
      const authError = err as AuthError;
      // Manual cleanup when Supabase can't find its own session
      if (authError.message?.includes('Auth session missing')) {
        // Clear all auth and user-specific storage
        clearUserStorage(true);

        // Clear user state
        setUser(null);
        setSession(null);

        // Redirect to home
        window.location.href = '/';
        return;
      }
      setError(authError.message);
      throw err;
    } finally {
      setLoading(false);
      // Reset the logging out flag
      setLoggingOut(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextValue = useMemo(() => ({
    // Auth state
    user,
    session,
    loading,
    error,

    // Passwordless auth methods
    signInWithGoogle,
    sendEmailOtp,
    verifyEmailOtp,
    signOut,

    // Utility methods
    clearError,

    // Helper flags
    isAuthenticated: !!user,
    isLoading: loading,
    hasError: !!error
  }), [user, session, loading, error, signInWithGoogle, sendEmailOtp, verifyEmailOtp, signOut, clearError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
export { AuthContext };
